import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenueId } from 'selectors/general';

import { buildIndicatorMap } from './ScheduleCalendar.helpers';

export const calendarIndicatorsKeys = {
  all: ['calendarIndicators'],
  range: (venueId, startDate, endDate) => [...calendarIndicatorsKeys.all, venueId, startDate, endDate],
};

export const CALENDAR_INDICATORS_STALE_TIME_MS = 5 * 60 * 1000;

export async function fetchCalendarIndicators({ venueId, startDate, endDate }) {
  try {
    const response = await httpClient.get(URLS.venue.calendarIndicators(venueId, startDate, endDate), {
      ignoreInternalError: true,
    });
    const data = transformKeysToCamelCase(response?.data?.data) || [];
    return buildIndicatorMap(data);
  } catch (error) {
    reportError('Failed to fetch calendar indicators', { error, venueId, startDate, endDate });
    throw error;
  }
}

export function useCalendarIndicators({ startDate, endDate } = {}, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: calendarIndicatorsKeys.range(venueId, startDate, endDate),
    queryFn: () => fetchCalendarIndicators({ venueId, startDate, endDate }),
    enabled: Boolean(venueId && startDate && endDate),
    retry: 1,
    staleTime: CALENDAR_INDICATORS_STALE_TIME_MS,
    ...options,
  });
}
